import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import informesPersonalizadosService from '../../services/informesPersonalizadosService';
import programadorEnviosService, {
  FrecuenciaEnvio,
  PeriodoEnvio,
  ProgramadorEnvio,
  ProgramadorEnvioConfig,
  ProgramadorEnvioEjecucion,
  TipoEnvioProgramado,
} from '../../services/programadorEnviosService';
import type { ModuloInformePersonalizado } from '../../utils/informePersonalizadoDefinicion';
import { getApiErrorMessage } from '../../utils/apiError';

const DIAS = [
  { id: 1, label: 'Lun' },
  { id: 2, label: 'Mar' },
  { id: 3, label: 'Mié' },
  { id: 4, label: 'Jue' },
  { id: 5, label: 'Vie' },
  { id: 6, label: 'Sáb' },
  { id: 7, label: 'Dom' },
];

const TIPOS: { id: TipoEnvioProgramado; label: string }[] = [
  { id: 'prueba', label: 'Correo de prueba' },
  { id: 'informe_personalizado', label: 'Informe personalizado' },
  { id: 'programacion_produccion', label: 'Programación de producción' },
  { id: 'ordenes_pago_dia', label: 'Órdenes de pago del período' },
];

const PERIODOS: { id: PeriodoEnvio; label: string }[] = [
  { id: 'hoy', label: 'Hoy' },
  { id: 'ayer', label: 'Ayer' },
  { id: 'semana_actual', label: 'Semana actual' },
  { id: 'semana_anterior', label: 'Semana anterior' },
  { id: 'mes_actual', label: 'Mes actual' },
  { id: 'mes_anterior', label: 'Mes anterior' },
  { id: 'ultimos_n_dias', label: 'Últimos N días' },
];

const MODULOS: ModuloInformePersonalizado[] = ['Ventas', 'Compras', 'Contabilidad', 'Tesoreria'];

const configVacia = (): ProgramadorEnvioConfig => ({
  destinatarios: [],
  cc: [],
  asunto: '',
  cuerpo: '',
  siVacio: 'omitir',
  informeId: null,
  informeModulo: 'Ventas',
  recalcularAntes: false,
  codigoRamo: '',
  codigoLinea: '',
  codigoClase: '',
  nroUbicacion: null,
  ventanaDias: 30,
  horizonteDias: 30,
  ajusteDemandaPct: 0,
  incluirYaEnviadas: false,
});

interface Borrador {
  nombre: string;
  activa: boolean;
  tipo: TipoEnvioProgramado;
  frecuencia: FrecuenciaEnvio;
  diasSemana: number[];
  diaMes: number;
  hora: number;
  minuto: number;
  periodo: PeriodoEnvio;
  ultimosNDias: number;
  configuracion: ProgramadorEnvioConfig;
  para: string;
  copia: string;
  moduloInforme: ModuloInformePersonalizado;
}

const borradorNuevo = (): Borrador => ({
  nombre: '',
  activa: true,
  tipo: 'informe_personalizado',
  frecuencia: 'semanal',
  diasSemana: [5],
  diaMes: 1,
  hora: 20,
  minuto: 0,
  periodo: 'semana_actual',
  ultimosNDias: 7,
  configuracion: configVacia(),
  para: '',
  copia: '',
  moduloInforme: 'Ventas',
});

const partirMails = (texto: string) =>
  texto.split(/[;,]/).map((item) => item.trim()).filter(Boolean);

const etiquetaTipo = (tipo: string) => TIPOS.find((item) => item.id === tipo)?.label ?? tipo;

const etiquetaEstado = (estado: string | null) => {
  switch (estado) {
    case 'ok': return 'Enviado';
    case 'parcial': return 'Parcial';
    case 'omitida': return 'Sin datos';
    case 'error': return 'Error';
    case 'en_curso': return 'En curso';
    default: return estado || '—';
  }
};

const fmt = (valor: string | null) => {
  if (!valor) return '—';
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return valor;
  return fecha.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
};

const ProgramadorEnvios: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<ProgramadorEnvio[]>([]);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState<number | null>(null);
  const [borrador, setBorrador] = useState<Borrador | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [informes, setInformes] = useState<{ id: number; titulo: string }[]>([]);
  const [historial, setHistorial] = useState<{ nombre: string; filas: ProgramadorEnvioEjecucion[] } | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setItems(await programadorEnviosService.list());
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron cargar los envíos.'), { variant: 'error' });
    } finally {
      setCargando(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  useEffect(() => {
    if (!borrador || borrador.tipo !== 'informe_personalizado') return;
    let cancelado = false;
    void informesPersonalizadosService.list(borrador.moduloInforme)
      .then((lista) => {
        if (!cancelado) setInformes(lista.map((item) => ({ id: item.id, titulo: item.titulo })));
      })
      .catch(() => {
        if (!cancelado) setInformes([]);
      });
    return () => {
      cancelado = true;
    };
  }, [borrador?.tipo, borrador?.moduloInforme]);

  const abrirNuevo = () => {
    setEditando(null);
    setBorrador(borradorNuevo());
  };

  const abrirEdicion = (item: ProgramadorEnvio) => {
    setEditando(item.id);
    setBorrador({
      nombre: item.nombre,
      activa: item.activa,
      tipo: item.tipo,
      frecuencia: item.frecuencia,
      diasSemana: item.diasSemana,
      diaMes: item.diaMes ?? 1,
      hora: item.hora,
      minuto: item.minuto === 30 ? 30 : 0,
      periodo: item.periodo,
      ultimosNDias: item.ultimosNDias,
      configuracion: {
        ...configVacia(),
        ...item.configuracion,
        informeModulo: item.configuracion?.informeModulo || 'Ventas',
      },
      para: (item.configuracion?.destinatarios ?? []).join(', '),
      copia: (item.configuracion?.cc ?? []).join(', '),
      moduloInforme: (item.configuracion?.informeModulo as ModuloInformePersonalizado) || 'Ventas',
    });
  };

  const cambiarTipo = (tipo: TipoEnvioProgramado) => {
    setBorrador((prev) => {
      if (!prev) return prev;
      if (tipo === 'informe_personalizado') {
        return { ...prev, tipo, frecuencia: 'semanal', diasSemana: [5], hora: 20, minuto: 0, periodo: 'semana_actual' };
      }
      if (tipo === 'programacion_produccion') {
        return { ...prev, tipo, frecuencia: 'semanal', diasSemana: [1], hora: 9, minuto: 0, periodo: 'hoy' };
      }
      if (tipo === 'ordenes_pago_dia') {
        return { ...prev, tipo, frecuencia: 'diaria', diasSemana: [], hora: 3, minuto: 0, periodo: 'ayer' };
      }
      return { ...prev, tipo, frecuencia: 'diaria', hora: 9, minuto: 0, periodo: 'hoy' };
    });
  };

  const guardar = async () => {
    if (!borrador) return;
    setGuardando(true);
    const payload = {
      nombre: borrador.nombre.trim(),
      activa: borrador.activa,
      tipo: borrador.tipo,
      frecuencia: borrador.frecuencia,
      diasSemana: borrador.diasSemana,
      diaMes: borrador.diaMes,
      hora: Number(borrador.hora),
      minuto: Number(borrador.minuto) === 30 ? 30 : 0,
      periodo: borrador.periodo,
      ultimosNDias: Number(borrador.ultimosNDias) || 7,
      configuracion: {
        ...borrador.configuracion,
        destinatarios: partirMails(borrador.para),
        cc: partirMails(borrador.copia),
      },
    };
    try {
      if (editando == null) await programadorEnviosService.crear(payload);
      else await programadorEnviosService.actualizar(editando, payload);
      enqueueSnackbar('Envío guardado.', { variant: 'success' });
      setBorrador(null);
      await cargar();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo guardar el envío.'), { variant: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  const pausar = async (item: ProgramadorEnvio) => {
    try {
      await programadorEnviosService.cambiarActiva(item.id, !item.activa);
      await cargar();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo actualizar el envío.'), { variant: 'error' });
    }
  };

  const ejecutar = async (item: ProgramadorEnvio) => {
    try {
      const resultado = await programadorEnviosService.ejecutar(item.id);
      enqueueSnackbar(`Ejecución ${etiquetaEstado(resultado.estado).toLowerCase()}.`, {
        variant: resultado.estado === 'error' ? 'error' : 'success',
      });
      await cargar();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo ejecutar el envío.'), { variant: 'error' });
    }
  };

  const verHistorial = async (item: ProgramadorEnvio) => {
    try {
      const filas = await programadorEnviosService.historial(item.id);
      setHistorial({ nombre: item.nombre, filas });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo leer el historial.'), { variant: 'error' });
    }
  };

  const eliminar = async (item: ProgramadorEnvio) => {
    if (!window.confirm(`¿Eliminar "${item.nombre}"?`)) return;
    try {
      await programadorEnviosService.eliminar(item.id);
      enqueueSnackbar('Envío eliminado.', { variant: 'success' });
      await cargar();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo eliminar el envío.'), { variant: 'error' });
    }
  };

  const pideDestino = borrador?.tipo !== 'ordenes_pago_dia';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Programador de envíos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Horarios en hora de Argentina, en puntos :00 o :30. El sistema revisa pendientes cada 30 minutos.
                  El correo sale por la cuenta configurada en Envío de correos.
                </Typography>
              </Box>
              <Button variant="contained" onClick={abrirNuevo}>Nuevo envío</Button>
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Próxima</TableCell>
                  <TableCell>Última</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>{cargando ? 'Cargando…' : 'Todavía no hay envíos programados.'}</TableCell>
                  </TableRow>
                )}
                {items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      {item.nombre}
                      {!item.activa && <Chip size="small" label="Pausado" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>{etiquetaTipo(item.tipo)}</TableCell>
                    <TableCell>{fmt(item.proximaEjecucion)}</TableCell>
                    <TableCell>{fmt(item.ultimaEjecucion)}</TableCell>
                    <TableCell>{etiquetaEstado(item.ultimoEstado)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => void ejecutar(item)}>Ejecutar ahora</Button>
                      <Button size="small" onClick={() => void verHistorial(item)}>Historial</Button>
                      <Button size="small" onClick={() => void pausar(item)}>{item.activa ? 'Pausar' : 'Activar'}</Button>
                      <Button size="small" onClick={() => abrirEdicion(item)}>Editar</Button>
                      <Button size="small" color="error" onClick={() => void eliminar(item)}>Eliminar</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={borrador != null} onClose={() => setBorrador(null)} fullWidth maxWidth="md">
        <DialogTitle>{editando == null ? 'Nuevo envío' : 'Editar envío'}</DialogTitle>
        <DialogContent>
          {borrador && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Nombre"
                value={borrador.nombre}
                onChange={(event) => setBorrador({ ...borrador, nombre: event.target.value })}
                fullWidth
                required
              />
              <FormControl fullWidth>
                <InputLabel>Qué enviar</InputLabel>
                <Select
                  label="Qué enviar"
                  value={borrador.tipo}
                  onChange={(event) => cambiarTipo(event.target.value as TipoEnvioProgramado)}
                >
                  {TIPOS.map((tipo) => <MenuItem key={tipo.id} value={tipo.id}>{tipo.label}</MenuItem>)}
                </Select>
              </FormControl>

              {borrador.tipo === 'informe_personalizado' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Módulo</InputLabel>
                    <Select
                      label="Módulo"
                      value={borrador.moduloInforme}
                      onChange={(event) => setBorrador({
                        ...borrador,
                        moduloInforme: event.target.value as ModuloInformePersonalizado,
                        configuracion: {
                          ...borrador.configuracion,
                          informeId: null,
                          informeModulo: event.target.value,
                        },
                      })}
                    >
                      {MODULOS.map((modulo) => <MenuItem key={modulo} value={modulo}>{modulo}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Informe</InputLabel>
                    <Select
                      label="Informe"
                      value={borrador.configuracion.informeId ?? ''}
                      onChange={(event) => setBorrador({
                        ...borrador,
                        configuracion: {
                          ...borrador.configuracion,
                          informeId: Number(event.target.value),
                          informeModulo: borrador.moduloInforme,
                        },
                      })}
                    >
                      {informes.map((informe) => (
                        <MenuItem key={informe.id} value={informe.id}>{informe.titulo}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              )}

              {borrador.tipo === 'programacion_produccion' && (
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={borrador.configuracion.recalcularAntes}
                      onChange={(event) => setBorrador({
                        ...borrador,
                        configuracion: { ...borrador.configuracion, recalcularAntes: event.target.checked },
                      })}
                    />
                  )}
                  label="Recalcular la programación antes de enviar"
                />
              )}

              {borrador.tipo === 'ordenes_pago_dia' && (
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={borrador.configuracion.incluirYaEnviadas}
                      onChange={(event) => setBorrador({
                        ...borrador,
                        configuracion: { ...borrador.configuracion, incluirYaEnviadas: event.target.checked },
                      })}
                    />
                  )}
                  label="Incluir órdenes que ya se enviaron por correo"
                />
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Frecuencia</InputLabel>
                  <Select
                    label="Frecuencia"
                    value={borrador.frecuencia}
                    onChange={(event) => setBorrador({ ...borrador, frecuencia: event.target.value as FrecuenciaEnvio })}
                  >
                    <MenuItem value="diaria">Todos los días</MenuItem>
                    <MenuItem value="semanal">Días de la semana</MenuItem>
                    <MenuItem value="mensual">Una vez por mes</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Hora</InputLabel>
                  <Select
                    label="Hora"
                    value={borrador.hora}
                    onChange={(event) => setBorrador({ ...borrador, hora: Number(event.target.value) })}
                  >
                    {Array.from({ length: 24 }, (_, hora) => (
                      <MenuItem key={hora} value={hora}>
                        {String(hora).padStart(2, '0')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Minutos</InputLabel>
                  <Select
                    label="Minutos"
                    value={borrador.minuto === 30 ? 30 : 0}
                    onChange={(event) => setBorrador({ ...borrador, minuto: Number(event.target.value) })}
                  >
                    <MenuItem value={0}>00</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              {borrador.frecuencia === 'semanal' && (
                <FormGroup row>
                  {DIAS.map((dia) => (
                    <FormControlLabel
                      key={dia.id}
                      control={(
                        <Checkbox
                          checked={borrador.diasSemana.includes(dia.id)}
                          onChange={(event) => {
                            const dias = event.target.checked
                              ? [...borrador.diasSemana, dia.id]
                              : borrador.diasSemana.filter((id) => id !== dia.id);
                            setBorrador({ ...borrador, diasSemana: dias.sort((a, b) => a - b) });
                          }}
                        />
                      )}
                      label={dia.label}
                    />
                  ))}
                </FormGroup>
              )}

              {borrador.frecuencia === 'mensual' && (
                <TextField
                  label="Día del mes"
                  type="number"
                  value={borrador.diaMes}
                  onChange={(event) => setBorrador({ ...borrador, diaMes: Number(event.target.value) })}
                  inputProps={{ min: 1, max: 31 }}
                  helperText="Si el mes no tiene ese día, se usa el último."
                />
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>Período de los datos</InputLabel>
                  <Select
                    label="Período de los datos"
                    value={borrador.periodo}
                    onChange={(event) => setBorrador({ ...borrador, periodo: event.target.value as PeriodoEnvio })}
                  >
                    {PERIODOS.map((periodo) => <MenuItem key={periodo.id} value={periodo.id}>{periodo.label}</MenuItem>)}
                  </Select>
                </FormControl>
                {borrador.periodo === 'ultimos_n_dias' && (
                  <TextField
                    label="Cantidad de días"
                    type="number"
                    value={borrador.ultimosNDias}
                    onChange={(event) => setBorrador({ ...borrador, ultimosNDias: Number(event.target.value) })}
                    inputProps={{ min: 1, max: 366 }}
                  />
                )}
              </Stack>

              {pideDestino && (
                <TextField
                  label="Para"
                  value={borrador.para}
                  onChange={(event) => setBorrador({ ...borrador, para: event.target.value })}
                  fullWidth
                  helperText="Uno o varios correos, separados por coma."
                />
              )}
              <TextField
                label="Copia"
                value={borrador.copia}
                onChange={(event) => setBorrador({ ...borrador, copia: event.target.value })}
                fullWidth
              />
              <TextField
                label="Asunto"
                value={borrador.configuracion.asunto}
                onChange={(event) => setBorrador({
                  ...borrador,
                  configuracion: { ...borrador.configuracion, asunto: event.target.value },
                })}
                fullWidth
                helperText="Podés usar {fecha}, {empresa}, {cantidad} y {proveedor}."
              />
              <TextField
                label="Mensaje"
                value={borrador.configuracion.cuerpo}
                onChange={(event) => setBorrador({
                  ...borrador,
                  configuracion: { ...borrador.configuracion, cuerpo: event.target.value },
                })}
                fullWidth
                multiline
                minRows={3}
              />
              <FormControl fullWidth>
                <InputLabel>Si no hay datos</InputLabel>
                <Select
                  label="Si no hay datos"
                  value={borrador.configuracion.siVacio}
                  onChange={(event) => setBorrador({
                    ...borrador,
                    configuracion: { ...borrador.configuracion, siVacio: event.target.value as 'omitir' | 'enviar' },
                  })}
                >
                  <MenuItem value="omitir">No enviar</MenuItem>
                  <MenuItem value="enviar">Enviar igual, avisando que no hubo movimientos</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBorrador(null)}>Cancelar</Button>
          <Button variant="contained" onClick={() => void guardar()} disabled={guardando}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historial != null} onClose={() => setHistorial(null)} fullWidth maxWidth="md">
        <DialogTitle>Historial {historial ? `· ${historial.nombre}` : ''}</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Inicio</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Mails</TableCell>
                <TableCell>Detalle</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(historial?.filas.length ?? 0) === 0 && (
                <TableRow><TableCell colSpan={4}>Todavía no hay ejecuciones.</TableCell></TableRow>
              )}
              {historial?.filas.map((fila) => (
                <TableRow key={fila.id}>
                  <TableCell>{fmt(fila.inicio)}{fila.manual ? ' (manual)' : ''}</TableCell>
                  <TableCell>{etiquetaEstado(fila.estado)}</TableCell>
                  <TableCell>{fila.mensajesEnviados}</TableCell>
                  <TableCell>{fila.error || fila.destinatarios || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistorial(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProgramadorEnvios;
